import { inject, Injectable, Injector } from "@angular/core";
import { getLoggerFor } from "@tylertech/logging";
import { Subscription } from "rxjs";
import { WINDOW } from "@tylertech/shared-module";
import { cxWindow, LIVE_CHAT_ADAPTER, LiveChatMessageEntry } from "../interfaces";
import { ExceptionService } from "./exceptionService.g";
import { SecurityService } from "./security.service";
import { SystemInfoService } from "./systemInfo.service";
import type { ChatEventData, ChatSdk, LivechatThread, Message, MessageTextContent } from "@nice-devone/nice-cxone-chat-web-sdk";
import { BrowserNotificationService, BrowserNotificationKeys } from "./browserNotificationService";

type ChatSdkModule = typeof import("@nice-devone/nice-cxone-chat-web-sdk");

export const SupportAreas = ["utilities", "financials", "property_tax"] as const;
export type SupportArea = typeof SupportAreas[number];

/** Arguments the AI agent supplies to the escalate tool (schema in CxEscalationTool.getDefinition). */
export interface EscalateArgs extends Record<string, unknown>
{
    reason: string;
    conversationSummary: string;
    priority?: "low" | "medium" | "high" | "urgent";
    area?: SupportArea;
}

export interface EscalationResult
{
    success: boolean;
    message: string;
    error: string;
    escalationSummary: string;
    timestamp: string;
    receivedArgs: any;
}

interface LiveChatRecord
{
    channelId: string;
    threadId: string;
}

type LiveChatConnection = "connected" | "offline" | "failed";

@Injectable()
export class CxBridgeService
{
    private readonly log = getLoggerFor("CxBridgeService");
    private readonly $injector = inject(Injector);
    private readonly window = inject<cxWindow>(WINDOW);
    private readonly $systemInfoService = inject(SystemInfoService);
    private readonly $browserNotificationService = inject(BrowserNotificationService);
    private readonly $chatAdapter = inject(LIVE_CHAT_ADAPTER);
    private readonly $exceptionService = inject(ExceptionService);
    private readonly LiveChatKey = "cxBridgeLiveChat";
    private readonly PreviousChatKeys = "cxBridgePreviousChats";
    private readonly QueuePositionKey = "queuePosition";
    private readonly TypingIndicatorKey = "typingIndicator";
    private readonly EscalationMessagePrefix = "🔔 ESCALATED CASE";
    /** Shown when NICE sends an agent without usable name fields (blank profile or "Hide personal information"). */
    private readonly DefaultAgentName = "Tyler Support";
    private threadRecovered = false;
    private endingSession = false;

    private readonly ChannelIds: Record<string, string | Record<SupportArea, string>> = {
        "Incode10": {
            financials: "chat_2c51ff34-f856-41ee-8da5-e68947f002f8",
            utilities: "chat_d0a136aa-6056-4092-8acc-0d0302050e82",
            property_tax: "chat_60778939-668e-41f1-acfa-8fb601a5f2e9"
        },
        "Incode9": {
            financials: "chat_77217ad1-0e17-4f84-8eb4-17b20d2a3d70",
            utilities: "chat_9338c518-9a40-4b6f-b9c8-913862669eff",
            property_tax: "chat_3622bd9b-101f-4a47-bc2e-86dda69bcbcf"
        },
        "Infinite Visions": "chat_55685055-46aa-4123-9660-3f491c51f326"
    };

    private readonly ChannelsWithBrokenAvailability = [this.ChannelIds["Infinite Visions"] as string];

    get routesByArea(): boolean
    {
        return typeof this.ChannelIds[this.$systemInfoService.systemInfo?.Product] === "object";
    }

    private sdkApi: ChatSdkModule;
    private sdk: ChatSdk;
    private thread: LivechatThread;
    private contactId: string;
    private recoveredMessages: Message[] | null;
    private unsubscribers: Array<() => void> = [];
    private userMessageSubscription: Subscription;
    private userTypingSubscription: Subscription;

    // Lazy load Sdk
    protected async loadSdk(): Promise<ChatSdkModule>
    {
        const sdkModule: any = await import("@nice-devone/nice-cxone-chat-web-sdk");
        return (sdkModule.EnvironmentName ? sdkModule : sdkModule.default) as ChatSdkModule;
    }

    async executeEscalate(args: EscalateArgs): Promise<EscalationResult>
    {
        // Typed, but the values still originate from the model - keep the runtime guards.
        if (!args || typeof args !== "object")
        {
            this.log.error("[Escalate Tool] Invalid args received:", args);
            return <EscalationResult>{
                success: false,
                message: "Invalid arguments received",
                error: "Args must be an object"
            };
        }

        if (!args.reason || !args.conversationSummary)
        {
            this.log.error("[Escalate Tool] Invalid args: No reason or conversation summary");
            return <EscalationResult>{
                success: false,
                message: "Missing required fields: reason and conversationSummary are required",
                error: "Missing required fields",
                receivedArgs: args
            };
        }

        if (this.routesByArea && !(SupportAreas as readonly string[]).includes(args.area))
        {
            this.log.error("[Escalate Tool] Invalid args: missing or unknown area", args.area);
            return <EscalationResult>{
                success: false,
                message: `Missing or invalid area: call escalate again with area set to one of ${SupportAreas.join(", ")}.`,
                error: "Missing or invalid area",
                receivedArgs: args
            };
        }

        const escalationMessage = this.buildEscalationMessage(args);
        try
        {
            switch (await this.startEscalation(escalationMessage, args.area))
            {
                case "connected":
                    return <EscalationResult>{
                        success: true,
                        message: "Escalation accepted. Tell the user in one short sentence that you are connecting them to a live support agent in this chat window now. Do not ask any follow-up questions.",
                        escalationSummary: escalationMessage,
                        timestamp: new Date().toISOString()
                    };
                case "offline":
                    return <EscalationResult>{
                        success: false,
                        message: "Live support is offline right now, usually because it is outside support hours. Tell the user in one short sentence that live agents are not available right now and to try again during business hours. Do not call escalate again in this conversation unless the user asks.",
                        error: "Live support is offline",
                        escalationSummary: escalationMessage
                    };
                case "failed":
                default:
                    return <EscalationResult>{
                        success: false,
                        message: "Unable to connect to a live agent right now. Alternative contact methods have been provided on the page.",
                        error: "Live chat session could not be started",
                        escalationSummary: escalationMessage
                    };
            }
        }
        catch (error)
        {
            return <EscalationResult>{
                success: false,
                message: "Unable to connect to a live agent right now. Alternative contact methods have been provided on the page.",
                error: error.message,
                escalationSummary: escalationMessage
            };
        }
    }

    private buildEscalationMessage(args: EscalateArgs): string
    {
        const timestamp = new Date().toLocaleString();
        const priority = args.priority || "medium";
        let message = `${this.EscalationMessagePrefix} - Priority: ${priority.toUpperCase()}\n\n`;
        message += `Reason: ${args.reason}\n\n`;
        if (args.conversationSummary)
            message += `Conversation Summary:\n${args.conversationSummary}\n\n`;
        message += `---\n`;
        message += `Escalated at: ${timestamp}\n`;
        message += `Page URL: ${window.location.href}\n`;
        if (args.area)
            message += `Support area: ${args.area}\n`;
        message += `User Agent: ${navigator.userAgent}`;
        return message;
    }

    private async startEscalation(summary: string, area: SupportArea | undefined): Promise<LiveChatConnection>
    {
        if (this.$chatAdapter.isLive)
        {
            return "connected";
        }

        try
        {
            const principle = this.$injector.get(SecurityService).principle;
            this.threadRecovered = await this.resumeSavedLiveChat(principle);
            if (!this.threadRecovered)
            {
                const connection = await this.startNewLiveChat(area, principle);
                if (connection !== "connected")
                    return connection;
            }

            this.registerEvents();
            this.relayUserInput();

            void this.$chatAdapter.whenAIAgentRunSettles().then(reason =>
            {
                if (reason === "timeout")
                    this.log.warning("Timed out waiting for the AI agent run to settle - starting live session anyway.");
                return this.startLiveSession(summary);
            });
            return "connected";
        }
        catch (error)
        {
            this.reportFailure("Escalation to live chat failed.", error);
            await this.teardown();
            return "failed";
        }
    }

    async checkIfEscalationCanResume(): Promise<void>
    {
        if (this.$chatAdapter.isLive)
            return;

        try
        {
            const principle = this.$injector.get(SecurityService).principle;
            this.threadRecovered = await this.resumeSavedLiveChat(principle);
            if (!this.threadRecovered)
                return;

            this.registerEvents();
            this.relayUserInput();
            return this.startLiveSession();
        }
        catch (error)
        {
            this.reportFailure("Cannot resume escalation.  Clearing escalation.", error);
            await this.teardown();
            return;
        }
    }

    async populatePreviousEsclationChats(): Promise<void>
    {
        const previousChats = this.readPreviousEscalationChats();
        if (previousChats.length === 0)
        {
            this.log.debug("No previous sessions of the escalate.  Exiting.");
            return;
        }

        const endChatMessages = [this.getEndChatMessage("user"), this.getEndChatMessage("agent")];
        this.$chatAdapter.insertClientOnlyMessagesBeforeOtherMessages(endChatMessages, previousChats);
    }

    clearPreviousEscalationChats(): void
    {
        sessionStorage.removeItem(this.PreviousChatKeys);
    }

    private async resumeSavedLiveChat(principle: SecurityService["principle"]): Promise<boolean>
    {
        const savedLiveChat = this.readLiveChat();
        if (!savedLiveChat || await this.openChannel(savedLiveChat.channelId, principle) === "offline")
            return false;

        this.thread = this.sdk.getThread(savedLiveChat.threadId) as LivechatThread;
        if (await this.tryRecoverThread())
        {
            return true;
        }

        this.thread = null;
        this.closeChannel();
        return false;
    }

    private async startNewLiveChat(area: SupportArea | undefined, principle: SecurityService["principle"]): Promise<LiveChatConnection>
    {
        sessionStorage.removeItem(this.LiveChatKey);
        const channelId = this.resolveChannelId(area);
        const connection = await this.openChannel(channelId, principle);
        if (connection !== "connected")
            return connection;

        const threadId = this.sdkApi.generateId();
        this.thread = this.sdk.getThread(threadId) as LivechatThread;
        sessionStorage.setItem(this.LiveChatKey, JSON.stringify(<LiveChatRecord>{ channelId, threadId }));
        return "connected";
    }

    private resolveChannelId(area: SupportArea | undefined): string
    {
        const product = this.$systemInfoService.systemInfo.Product;
        const channels = this.ChannelIds[product];
        const channelId = typeof channels === "string" ? channels : area && channels?.[area];
        if (!channelId)
            throw new Error(`No NICE channel configured for product "${product}", area "${area}".`);
        return channelId;
    }

    private readLiveChat(): LiveChatRecord | null
    {
        try
        {
            return JSON.parse(sessionStorage.getItem(this.LiveChatKey)) as LiveChatRecord | null;
        }
        catch (error)
        {
            this.reportFailure("Saved live chat record is unreadable - starting a new chat.", error);
            return null;
        }
    }

    /** Gets the previous escalation chats.  These should be in order from the first to last. */
    private readPreviousEscalationChats(): LiveChatMessageEntry[][]
    {
        try
        {
            const previousChats = JSON.parse(sessionStorage.getItem(this.PreviousChatKeys)) as LiveChatMessageEntry[][] | null;
            return Array.isArray(previousChats) ? previousChats.filter(Array.isArray) : [];
        }
        catch (error)
        {
            this.reportFailure("Saved previous chat messages are unreadable - ignoring them.", error);
            return [];
        }
    }

    private addPreviousEscalationChatToSessionStorage(messages: Message[] | null): void
    {
        try
        {
            const entries = this.toMessageEntries(messages);
            const previousChats = this.readPreviousEscalationChats();
            previousChats.push(entries);
            sessionStorage.setItem(this.PreviousChatKeys, JSON.stringify(previousChats));
        }
        catch (error)
        {
            this.reportFailure("Unable to save the ended chat to the previous chats.", error);
        }
    }

    private getEndChatMessage(type: "user" | "agent"): string
    {
        if (type === "user")
            return "I ended the chat with the live agent.";
        else
            return "The agent helped with my issue, and they resolved our chat.";
    }

    private async saveEndedChat(): Promise<void>
    {
        try
        {
            const recovered = await this.withTimeout(this.thread.recover(), 2000);
            this.addPreviousEscalationChatToSessionStorage(recovered.messages);
        }
        catch (error)
        {
            this.reportFailure("Unable to read the messages of the ended chat.", error);
        }
    }

    private async openChannel(channelId: string, principle: SecurityService["principle"]): Promise<LiveChatConnection>
    {
        if (this.sdk)
            throw new Error("A NICE connection is already open.");

        await this.createSdk(channelId, principle);
        const connection = await this.connectToVerifiedLivechatChannel();
        if (connection === "offline")
            this.sdk = null;
        else
            this.identifyCustomer(principle);
        return connection;
    }

    private async createSdk(channelId: string, principle: SecurityService["principle"]): Promise<void>
    {
        this.sdkApi = await this.loadSdk();

        this.sdk = new this.sdkApi.ChatSdk({
            appName: "Tyler ExpressWeb Assistant Bridge",
            brandId: Number(this.window.BRAND_ID || "4189"),
            channelId,
            customerId: principle.Email,
            environment: this.sdkApi.EnvironmentName.NA1,
            isLivechat: true,
            storage: window.localStorage,
            cacheStorage: new this.sdkApi.CacheStorage(window.localStorage),
            onError: (error) =>
            {
                this.log.error("NICE SDK reported an error.", error);
            }
        });
    }

    private async connectToVerifiedLivechatChannel(): Promise<LiveChatConnection>
    {
        const availability = await this.sdk.getChannelAvailability();
        if (availability.status !== this.sdkApi.ChannelAvailability.ONLINE
            && !this.ChannelsWithBrokenAvailability.includes(this.sdk.channelId))
            return "offline";

        await this.sdk.connect();

        const info = await this.sdk.getChannelInfo();
        if (!info.isLiveChat)
            throw new Error(`NICE channel ${this.sdk.channelId} is not a live-chat channel.`);
        return "connected";
    }

    private identifyCustomer(principle: SecurityService["principle"]): void
    {
        const customer = this.sdk.getCustomer();
        customer.setName(principle.Fullname);
        void customer.setCustomFields({
            name: principle.Fullname,
            email: principle.Email,
            city: principle.Settings.Organization ?? "",
            region: principle.Settings.State ?? ""
        });
    }

    private relayUserInput(): void
    {
        this.userMessageSubscription = this.$chatAdapter.onUserTypedMessage
            .subscribe(text =>
            {
                void this.thread.sendTextMessage(text);
                void this.thread.stopTyping();
            });
        this.userTypingSubscription = this.$chatAdapter.onUserTyping
            .subscribe(() => void this.thread.keystroke());
    }

    private registerEvents(): void
    {
        const api = this.sdkApi;

        this.unsubscribers.push(
            this.thread.onThreadEvent(api.ChatEvent.MESSAGE_CREATED, event => this.relayAgentMessage(event)),
            this.sdk.onChatEvent(api.ChatEvent.SET_POSITION_IN_QUEUE, event => this.announceQueuePosition(event)),
            this.sdk.onChatEvent(api.ChatEvent.ASSIGNED_AGENT_CHANGED, event => this.announceAgentJoined(event)),
            this.sdk.onChatEvent(api.ChatEvent.AGENT_TYPING_STARTED, event => this.showAgentTyping(event)),
            this.sdk.onChatEvent(api.ChatEvent.AGENT_TYPING_ENDED, event => this.hideAgentTyping(event)),
            this.sdk.onChatEvent(api.ChatEvent.CONTACT_STATUS_CHANGED, event => this.handleContactStatusChanged(event))
        );
    }

    private relayAgentMessage(event: CustomEvent<ChatEventData>): void
    {
        if (!this.sdkApi.isMessageCreatedEvent(event.detail))
            return;
        const message = event.detail.data.message;
        if (message.direction === "inbound")
            return;

        const text = this.getMessageText(message);
        const agentName = this.formatUserName(message.authorUser);

        // Outbound == from the contact center, so it is always an agent message regardless of whether NICE gave us a name.
        this.$chatAdapter.appendClientOnlyMessage("assistant", `**${this.agentLabel(agentName)}:** ${text}`, this.TypingIndicatorKey);
        this.notifyUser("Agent Message", `${this.agentLabel(agentName)} has sent a message.`);
        if (document.hasFocus())
            void this.thread.lastMessageSeen();
    }

    //Calls the various ways to notify a user (browser notification, toast and audio, closed counter)
    private notifyUser(title: string, message: string): void
    {
        const isClosed = !this.$chatAdapter.isOpen;
        this.$browserNotificationService.sendNotification(title, message, isClosed, BrowserNotificationKeys.SMARTPULSE_NOTIFICATION_KEY);
        if (isClosed)
            this.$browserNotificationService.incrementNotificationCountForKey(BrowserNotificationKeys.SMARTPULSE_NOTIFICATION_KEY);
    }

    private announceQueuePosition(event: CustomEvent<ChatEventData>): void
    {
        if (!this.sdkApi.isSetPositionInQueueEvent(event.detail))
            return;
        const position = event.detail.data.positionInQueue;
        if (position)
        {
            const queuePositionMessage = `You are #${position} in the queue.`;
            this.$chatAdapter.upsertClientOnlyMessage(this.QueuePositionKey, "system", queuePositionMessage);
            this.notifyUser("Placed in Support Queue", queuePositionMessage);
        }
    }

    private announceAgentJoined(event: CustomEvent<ChatEventData>): void
    {
        if (!this.sdkApi.isAssignedAgentChangedEvent(event.detail))
            return;
        const { inboxAssignee, previousInboxAssignee } = event.detail.data;
        const name = this.formatUserName(inboxAssignee);
        // A null assignee means the contact was unassigned (e.g. back to queue) - nothing to announce.
        const joinMessage = `${this.agentLabel(name)} has joined the chat.`;
        if (inboxAssignee && inboxAssignee.id !== previousInboxAssignee?.id)
        {
            this.$chatAdapter.removeClientOnlyMessage(this.QueuePositionKey);
            this.$chatAdapter.appendClientOnlyMessage("system", `${this.agentLabel(name)} has joined the chat.`);
            this.notifyUser("Agent Joined", joinMessage);
        }
    }

    private showAgentTyping(event: CustomEvent<ChatEventData>): void
    {
        if (!this.sdkApi.isAgentTypingStartedEvent(event.detail))
            return;
        const name = this.formatUserName(event.detail.data.user);
        this.$chatAdapter.upsertClientOnlyMessage(this.TypingIndicatorKey, "system", `${this.agentLabel(name)} is typing...`);
    }

    private hideAgentTyping(event: CustomEvent<ChatEventData>): void
    {
        if (!this.sdkApi.isAgentTypingEndedEvent(event.detail))
            return;
        this.$chatAdapter.removeClientOnlyMessage(this.TypingIndicatorKey);
    }

    private handleContactStatusChanged(event: CustomEvent<ChatEventData>): void
    {
        if (!this.sdkApi.isContactStatusChangedEvent(event.detail))
            return;
        const contact = event.detail.data.case;
        if (contact?.contactId)
            this.contactId = String(contact.contactId);
        if (contact?.status === this.sdkApi.ContactStatus.CLOSED || contact?.status === this.sdkApi.ContactStatus.RESOLVED)
            void this.endLiveSession("agent");
    }

    private async startLiveSession(summary?: string): Promise<void>
    {
        try
        {
            this.$chatAdapter.enterLiveMode(() => void this.endLiveSession("user"));
            this.$chatAdapter.show();
            window.addEventListener("focus", this.markSeenOnFocus);
            if (this.threadRecovered)
                this.setRecoveredMessage();

            if (summary)
            {
                if (this.threadRecovered)
                    await this.thread.sendTextMessage(summary);
                else
                    await this.thread.startChat(summary);
            }
        }
        catch (error)
        {
            this.reportFailure("Live session start failed after escalation was accepted.", error);
            await this.teardown();
            this.$chatAdapter.appendClientOnlyMessage("system",
                "We couldn't reach a live agent just now. Please try again, or contact support directly.");
        }
    }

    //On a refresh, messages get lost as we store locally.  We need to retrieve back and reset them.
    private setRecoveredMessage(): void
    {
        try
        {
            this.$chatAdapter.appendMultipleClientOnlyMessages(this.toMessageEntries(this.recoveredMessages));
            this.recoveredMessages = null;
        }
        catch (error)
        {
            this.reportFailure("Unable to restore any messages from chat history.", error);
        }
    }

    private toMessageEntries(messages: Message[] | null): LiveChatMessageEntry[]
    {
        const entries: LiveChatMessageEntry[] = [];
        if (!messages || messages.length === 0)
            return [];

        for (const message of messages.sort((a, b) => Date.parse(a.createdAt) - Date.parse(b.createdAt)))
        {
            try
            {
                const text = this.getMessageText(message);
                if (!text || text.startsWith(this.EscalationMessagePrefix))
                    continue;

                if (message.direction === "inbound")
                    entries.push({ role: "user", content: text });
                else
                    entries.push({ role: "assistant", content: `**${this.agentLabel(this.formatUserName(message.authorUser))}:** ${text}` });
            }
            catch (error)
            {
                this.reportFailure("Unable to restore a message from chat history.", error);
            }
        }
        return entries;
    }

    async endLiveSession(reason: "user" | "agent"): Promise<void>
    {
        if (!this.thread || this.endingSession)
            return;
        this.endingSession = true;

        // Leave live mode (remove End chat button, restore the prompt) before any network call so the UI
        // never waits on NICE. The SDK's sendChatEvent only resolves when NICE echoes the event id back,
        // and NICE does not answer EndContact, so every send below is bounded by a timeout.
        this.$chatAdapter.leaveLiveMode();

        try
        {
            this.$chatAdapter.appendMessage("system", "Live chat with Tyler Support has ended. You're back with the AI Assistant.");
            this.notifyUser("Chat Ended", "The Live chat with Tyler Support has ended.  You're back with the AI Assistant.");

            await this.saveEndedChat();

            if (reason === "user")
            {
                try
                {
                    await this.withTimeout(this.thread.endChat(), 2000);
                }
                catch (error)
                {
                    this.reportFailure("endChat failed - sending EndContact directly.", error);
                    await this.sendEndContactFallback();
                }
            }
        }
        finally
        {
            await this.teardown();
            try
            {
                await this.$chatAdapter.sendDirectMessageToAIAgent(this.getEndChatMessage(reason));
            }
            catch (error)
            {
                this.reportFailure("Unable to tell the AI agent the live session ended.", error);
            }
        }
    }

    private async tryRecoverThread(): Promise<boolean>
    {
        try
        {
            const recovered = await this.thread.recover();
            const contact = recovered.contact;
            if (!contact)
            {
                return false;
            }
            if (contact.status === this.sdkApi.ContactStatus.CLOSED || contact?.status === this.sdkApi.ContactStatus.RESOLVED)
            {
                this.addPreviousEscalationChatToSessionStorage(recovered.messages);
                sessionStorage.removeItem(this.LiveChatKey);
                this.$chatAdapter.sendDirectMessageToAIAgent(this.getEndChatMessage("agent"));
                return false;
            }

            this.contactId = String(contact.contactId ?? contact.id);
            this.recoveredMessages = recovered.messages;
            return true;
        }
        catch (error)
        {
            if (error instanceof this.sdkApi.ThreadRecoverFailedError)
                return false;
            throw error;
        }
    }

    private withTimeout<T>(promise: Promise<T>, milliseconds: number): Promise<T>
    {
        return Promise.race([
            promise,
            new Promise<never>((_, reject) => window.setTimeout(() => reject(new Error(`Timed out after ${milliseconds}ms.`)), milliseconds))
        ]);
    }

    private getMessageText(message: Message): string
    {
        const payload = (message.messageContent as MessageTextContent).payload;
        if (typeof payload?.text === "string")
            return payload.text;
        return message.messageContent.fallbackText ?? "";
    }

    /** Best-effort display name from a NICE user; empty when the profile is blank or the fields were hidden. */
    private formatUserName(user: unknown): string
    {
        const author = user as { firstName?: string; surname?: string; nickname?: string; loginUsername?: string } | undefined;
        if (!author)
            return "";
        const fullName = `${author.firstName ?? ""} ${author.surname ?? ""}`.trim();
        return fullName || author.nickname || author.loginUsername || "";
    }

    /** "Jane Doe (Tyler Support)" when NICE gave us a name, otherwise just "Tyler Support". */
    private agentLabel(name: string): string
    {
        return name ? `${name} (${this.DefaultAgentName})` : this.DefaultAgentName;
    }

    private async sendEndContactFallback(): Promise<void>
    {
        try
        {
            const threadId = this.readLiveChat()?.threadId;
            if (!this.contactId || !threadId)
                return;
            await this.withTimeout(
                this.sdkApi.sendChatEvent(
                    { eventType: "EndContact", data: { thread: { idOnExternalPlatform: threadId }, contact: { id: this.contactId } } } as any,
                    this.sdk.getWebsocketClient()),
                2000);
        }
        catch (error)
        {
            this.reportFailure("EndContact fallback failed - session may remain open on the agent side.", error);
        }
    }

    private readonly markSeenOnFocus = (): void =>
    {
        void this.thread?.lastMessageSeen();
    };

    private closeChannel(): void
    {
        const sdk = this.sdk;
        this.sdk = null;
        sdk.getWebsocketClient()?.disconnect();
    }

    private reportFailure(message: string, error: unknown): void
    {
        const reported = error instanceof Error ? error : new Error(String(error));
        this.log.error(message, reported);
        void this.$exceptionService.LogException({ message: `${message} ${reported.message}`, stack: `${message}\n${reported.stack}` });
    }

    async teardown(): Promise<void>
    {
        window.removeEventListener("focus", this.markSeenOnFocus);
        this.$chatAdapter.removeClientOnlyMessage(this.TypingIndicatorKey);
        this.unsubscribers.forEach(unsubscribe => unsubscribe());
        this.unsubscribers = [];
        this.userMessageSubscription?.unsubscribe();
        this.userMessageSubscription = null;
        this.userTypingSubscription?.unsubscribe();
        this.userTypingSubscription = null;
        this.$chatAdapter.leaveLiveMode();
        sessionStorage.removeItem(this.LiveChatKey);
        if (this.sdk)
            this.closeChannel();
        this.thread = null;
        this.contactId = null;
        this.recoveredMessages = null;
        this.threadRecovered = false;
        this.endingSession = false;
    }
}
